// src/components/corkboard/CorkCard.jsx
//
// One pinned index card on the Cork Board: an actor, a scene or an artifact.
//
// The card owns the drag, and the drag is the only thing here worth reading.
// Three decisions:
//
//   * **a drag reports twice, and only the second one writes.** The card moves
//     itself from local state, so its own position never waits on a round trip
//     through the page. `onDragMove` fires as it goes, because the strings are
//     drawn a level up and have to stay tied to the pin. `onMoved` fires once,
//     on drop, and that is the only one that reaches the server — saving during
//     a pointermove stream would broadcast to every collaborator sixty times a
//     second.
//
//   * **pointer capture, not window listeners.** `setPointerCapture` keeps the
//     events coming to this card even when the pointer outruns it, which it
//     will — and it cleans itself up when the pointer is released, including
//     the releases a window listener would miss (a pointercancel from a browser
//     gesture, an alt-tab mid-drag).
//
//   * **a click is a drag that did not move.** Both gestures start with
//     pointerdown on the card, so the card distinguishes them by distance
//     rather than by a separate onClick, which would otherwise fire at the end
//     of every drag and re-select the card the author was only moving. The
//     threshold exists because a mouse moves a pixel or two during an ordinary
//     click.
import React, { useCallback, useRef, useState } from 'react';
import { Box } from '@mui/material';

import { CARD_W, clampToStage } from '../../utils/corkBoard';
import { CB, FONT, TYPE_LABEL, cardFor } from './corkTokens';

// How far the pointer may travel and still count as a click rather than a drag.
const CLICK_SLOP_PX = 4;

export default function CorkCard({
  node,             // { _id, type, name, desc, x, y }
  selected,
  dimmed,
  stageRef,         // the board element, for clamping and coordinate maths
  onSelect,         // (nodeId) => void
  onDragMove,       // (nodeId, { x, y } | null) => void — live, so the strings follow
  onMoved,          // (nodeId, { x, y }) => void — on drop only; this one writes
}) {
  const skin = cardFor(node.type);
  const [drag, setDrag] = useState(null); // { x, y } while dragging, else null
  const grab = useRef(null);              // pointer offset within the card

  const handlePointerDown = useCallback((e) => {
    // Let the description textarea and the like keep their own gestures; only
    // the card body is a drag handle.
    if (e.target.closest('input, textarea, button, select')) return;
    const stage = stageRef.current;
    if (!stage) return;
    const rect = e.currentTarget.getBoundingClientRect();
    grab.current = {
      dx: e.clientX - rect.left,
      dy: e.clientY - rect.top,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({ x: node.x, y: node.y });
  }, [node.x, node.y, stageRef]);

  const handlePointerMove = useCallback((e) => {
    const g = grab.current;
    const stage = stageRef.current;
    if (!g || !stage) return;
    if (!g.moved) {
      const far = Math.abs(e.clientX - g.startX) > CLICK_SLOP_PX ||
                  Math.abs(e.clientY - g.startY) > CLICK_SLOP_PX;
      if (!far) return;
      g.moved = true;
    }
    const stageRect = stage.getBoundingClientRect();
    const next = clampToStage(
      e.clientX - stageRect.left - g.dx,
      e.clientY - stageRect.top - g.dy,
      // scrollWidth/Height, not the rect: the board is scrollable, so a card may
      // legitimately sit past the visible edge.
      Math.max(stage.scrollWidth, stageRect.width),
      Math.max(stage.scrollHeight, stageRect.height),
    );
    setDrag(next);
    // The strings are drawn a level up, from board state this card has not
    // written to yet. Without this they would stay tied to where the card used
    // to be and snap across on drop.
    onDragMove?.(node._id, next);
  }, [node._id, onDragMove, stageRef]);

  const endDrag = useCallback((e) => {
    const g = grab.current;
    grab.current = null;
    const landed = drag;
    setDrag(null);
    onDragMove?.(node._id, null);
    if (!g) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already gone */ }
    if (g.moved && landed) onMoved?.(node._id, landed);
    else onSelect?.(node._id);
  }, [drag, node._id, onDragMove, onMoved, onSelect]);

  // A cancelled pointer (browser gesture, window blur) is a drag that never
  // finished: put the card back rather than committing a half-gesture.
  const cancelDrag = useCallback((e) => {
    grab.current = null;
    setDrag(null);
    onDragMove?.(node._id, null);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already gone */ }
  }, [node._id, onDragMove]);

  const left = drag ? drag.x : node.x;
  const top = drag ? drag.y : node.y;

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={cancelDrag}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect?.(node._id); }
      }}
      sx={{
        position: 'absolute',
        left, top,
        width: CARD_W,
        px: 1.75, pt: 2, pb: 1.5,
        backgroundColor: skin.bg,
        color: skin.ink,
        borderTop: `4px solid ${skin.edge}`,
        borderRadius: '2px',
        cursor: drag ? 'grabbing' : 'grab',
        userSelect: 'none',
        touchAction: 'none',
        opacity: dimmed ? 0.32 : 1,
        zIndex: drag ? 50 : (selected ? 20 : 10),
        boxShadow: drag
          ? '0 20px 30px -10px rgba(20,12,4,0.55)'
          : '0 8px 16px -6px rgba(20,12,4,0.45)',
        outline: selected ? `2px solid ${CB.goldLight}` : 'none',
        outlineOffset: '2px',
        // Only the look transitions. Animating `left`/`top` would make a
        // dragged card lag the pointer by the length of the transition.
        transition: 'box-shadow .15s ease, opacity .18s ease',
        '&:focus-visible': { outline: `2px solid ${CB.goldLight}`, outlineOffset: '2px' },
      }}
    >
      {/* The pin. Strings are tied here, which is why PIN_DX/PIN_DY in
          corkBoard.js and this offset have to describe the same point. */}
      <Box sx={{
        position: 'absolute', top: -7, left: '50%', transform: 'translateX(-50%)',
        width: 13, height: 13, borderRadius: '50%',
        background: `radial-gradient(circle at 35% 30%, ${CB.white}, ${skin.edge} 70%)`,
        boxShadow: '0 2px 3px rgba(0,0,0,0.5)',
      }} />

      <Box sx={{
        fontFamily: FONT.mono, fontSize: 8.5, letterSpacing: '.08em',
        textTransform: 'uppercase', opacity: 0.65,
      }}>
        {TYPE_LABEL[node.type] || node.type}
      </Box>

      <Box sx={{
        fontFamily: FONT.serif, fontSize: 16, fontWeight: 600, lineHeight: 1.2, mt: 0.25,
        overflowWrap: 'anywhere',
      }}>
        {node.name || 'Untitled'}
      </Box>

      {node.desc ? (
        <Box sx={{
          fontFamily: FONT.sans, fontSize: 11.5, lineHeight: 1.35, mt: 0.75, opacity: 0.8,
          // Two lines on the card; the rest lives in the side panel. A card that
          // grew with its description would overlap its neighbours, and the
          // board's whole job is spatial.
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {node.desc}
        </Box>
      ) : null}
    </Box>
  );
}
